import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../App';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import {
  FolderOpen, Folder, Plus, Upload, FileText, Image as ImageIcon, Trash2,
  Loader2, Download, ArrowLeft, FolderPlus
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DocumentFolders = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFolder, setActiveFolder] = useState(null);
  const [docs, setDocs] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const fileInputRef = useRef(null);
  const token = localStorage.getItem('token');

  const fetchFolders = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/document-folders`);
      setFolders(res.data || []);
    } catch (e) {
      toast.error('Failed to load document folders');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchDocs = useCallback(async (folderId) => {
    setDocsLoading(true);
    try {
      const res = await axios.get(`${API}/document-folders/${folderId}/documents`);
      setDocs(res.data || []);
    } catch (e) {
      toast.error('Failed to load documents');
    } finally {
      setDocsLoading(false);
    }
  }, []);

  useEffect(() => { fetchFolders(); }, [fetchFolders]);

  const openFolder = (folder) => {
    setActiveFolder(folder);
    fetchDocs(folder.id);
  };

  const backToFolders = () => {
    setActiveFolder(null);
    setDocs([]);
    fetchFolders();
  };

  const handleCreateFolder = async () => {
    const name = newFolderName.trim();
    if (!name) { toast.error('Enter a folder name'); return; }
    try {
      await axios.post(`${API}/document-folders`, { name });
      toast.success(`Folder "${name}" created`);
      setNewFolderName('');
      setShowNewFolder(false);
      fetchFolders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create folder');
    }
  };

  const handleDeleteFolder = async (folder) => {
    if (!window.confirm(`Delete folder "${folder.name}" and all its documents? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/document-folders/${folder.id}`);
      toast.success('Folder deleted');
      fetchFolders();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete folder');
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !activeFolder) return;
    if (file.size > 25 * 1024 * 1024) { toast.error('File too large (max 25 MB)'); return; }
    const form = new FormData();
    form.append('file', file);
    setUploading(true);
    try {
      await axios.post(`${API}/document-folders/${activeFolder.id}/documents`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Document uploaded');
      fetchDocs(activeFolder.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteDoc = async (docId) => {
    if (!window.confirm('Delete this document? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/documents/${docId}`);
      toast.success('Document deleted');
      setDocs((prev) => prev.filter((d) => d.id !== docId));
    } catch (e) {
      toast.error('Failed to delete document');
    }
  };

  const isImage = (d) => (d.content_type || '').startsWith('image/');
  const docUrl = (id) => `${API}/documents/${id}/download?auth=${token}`;

  // ---------- Folder grid ----------
  if (!activeFolder) {
    return (
      <div className="space-y-6 fade-in" data-testid="document-folders-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center space-x-3">
              <FolderOpen className="h-8 w-8 text-indigo-600" />
              <span>Document Center</span>
            </h1>
            <p className="text-gray-600 mt-1">Store shelter-wide policies, licenses and forms — securely, not on local disk.</p>
          </div>
          {isAdmin && (
            <Dialog open={showNewFolder} onOpenChange={setShowNewFolder}>
              <DialogTrigger asChild>
                <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="new-folder-btn">
                  <FolderPlus className="h-4 w-4 mr-2" /> New Folder
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="new-folder-dialog">
                <DialogHeader>
                  <DialogTitle>Create a new folder</DialogTitle>
                  <DialogDescription>Group related shelter documents (e.g. "Fire Inspections", "Grant Records").</DialogDescription>
                </DialogHeader>
                <Input
                  autoFocus
                  placeholder="Folder name"
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleCreateFolder(); }}
                  data-testid="new-folder-name-input"
                />
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setShowNewFolder(false)}>Cancel</Button>
                  <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={handleCreateFolder} data-testid="create-folder-submit">Create</Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(3)].map((_, i) => (
              <Card key={i} className="card-shadow"><CardContent className="p-6"><div className="skeleton h-20 w-full rounded" /></CardContent></Card>
            ))}
          </div>
        ) : folders.length === 0 ? (
          <Card className="card-shadow">
            <CardContent className="p-12 text-center">
              <Folder className="h-16 w-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No folders yet</h3>
              <p className="text-gray-500">{isAdmin ? 'Create your first folder to start storing shelter documents.' : 'Ask an administrator to set up document folders.'}</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {folders.map((folder) => (
              <Card
                key={folder.id}
                className="card-shadow hover:shadow-lg transition-shadow cursor-pointer group"
                onClick={() => openFolder(folder)}
                data-testid={`folder-card-${folder.id}`}
              >
                <CardContent className="p-6 flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className="p-3 rounded-lg bg-indigo-50 text-indigo-600 group-hover:bg-indigo-100 transition-colors">
                      <Folder className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{folder.name}</h3>
                      <p className="text-sm text-gray-500 mt-1">
                        {folder.document_count} {folder.document_count === 1 ? 'document' : 'documents'}
                      </p>
                    </div>
                  </div>
                  {isAdmin && (
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDeleteFolder(folder); }}
                      className="text-gray-300 hover:text-red-500 transition-colors"
                      data-testid={`delete-folder-${folder.id}`}
                      title="Delete folder"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ---------- Documents inside a folder ----------
  return (
    <div className="space-y-6 fade-in" data-testid="folder-detail-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <button onClick={backToFolders} className="text-sm text-indigo-600 hover:text-indigo-800 flex items-center mb-2" data-testid="back-to-folders-btn">
            <ArrowLeft className="h-4 w-4 mr-1" /> All folders
          </button>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center space-x-3">
            <FolderOpen className="h-8 w-8 text-indigo-600" />
            <span>{activeFolder.name}</span>
          </h1>
        </div>
        <div>
          <input ref={fileInputRef} type="file" accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.csv,.txt" onChange={handleUpload} className="hidden" data-testid="doc-file-input" />
          <Button onClick={() => fileInputRef.current?.click()} disabled={uploading} className="bg-indigo-600 hover:bg-indigo-700" data-testid="upload-doc-btn">
            {uploading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Uploading...</> : <><Upload className="h-4 w-4 mr-2" />Upload Document</>}
          </Button>
        </div>
      </div>

      {docsLoading ? (
        <div className="text-center py-10 text-gray-400"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
      ) : docs.length === 0 ? (
        <Card className="card-shadow">
          <CardContent className="p-12 text-center">
            <FileText className="h-16 w-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No documents in this folder</h3>
            <p className="text-gray-500">Upload a policy, license or form to get started.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {docs.map((d) => (
            <div key={d.id} className="border rounded-lg overflow-hidden bg-white card-shadow" data-testid={`doc-card-${d.id}`}>
              <div className="h-32 bg-gray-100 flex items-center justify-center overflow-hidden">
                {isImage(d) ? (
                  <a href={docUrl(d.id)} target="_blank" rel="noreferrer">
                    <img src={docUrl(d.id)} alt={d.original_filename} className="h-32 w-full object-cover" />
                  </a>
                ) : (
                  <a href={docUrl(d.id)} target="_blank" rel="noreferrer" className="flex flex-col items-center text-gray-500">
                    <FileText className="h-10 w-10" />
                  </a>
                )}
              </div>
              <div className="p-3 space-y-1">
                <p className="text-sm text-gray-700 truncate font-medium" title={d.original_filename}>{d.original_filename}</p>
                <p className="text-xs text-gray-400">by {d.uploaded_by}</p>
                <div className="flex items-center justify-between pt-1">
                  <a href={docUrl(d.id)} target="_blank" rel="noreferrer" className="text-indigo-600 hover:text-indigo-800" title="Open / download"><Download className="h-4 w-4" /></a>
                  <button onClick={() => handleDeleteDoc(d.id)} className="text-red-400 hover:text-red-600" data-testid={`delete-doc-${d.id}`} title="Delete"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DocumentFolders;
